<template>
    <div class="rich-text-editor">
        <label
            v-if="label"
            for="rich-text-editor"
            class="v-label custom-label"
            :style="editorFocused ? { color: 'rgb(0, 122, 255)' } : { color: 'rgb(133, 144, 161)' }"
        >
            {{ label }}
        </label>
        <quillEditor
            ref="richTextEditorContent"
            id="rich-text-editor"
            :value="content"
            :options="editorOption"
            :disabled="disabled"
            class="bubble"
            style="width: 100%"
            :class="[errorMessage ? 'ql-error' : '', classList]"
            @change="onInput"
            @focus="focus($event)"
            @blur="focus(false)"
        />
        <div
            v-if="errorMessage && !hideDetails"
            class="error--text caption"
        >
            {{ errorMessage }}
        </div>
    </div>
</template>

<script>
import sanitizeHtml from 'sanitize-html';
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";

import { quillEditor } from "vue-quill-editor";
import Quill from "quill";
import MagicUrl from "quill-magic-url";
import { onMounted, ref } from "@vue/composition-api";

export default {
    name: "RichTextEditor",
    components: {
        quillEditor
    },
    props: {
        content: {
            type: String,
            required: true
        },
        toolbar: {
            type: String,
            default: null
        },
        errorMessage: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        classList: {
            type: String,
            default: ""
        },
        placeholder: {
            type: String,
            default: ""
        },
        hideDetails: {
            type: Boolean,
            default: true
        },
        label: {
            type: String,
            default: null,
        }
    },
    setup(props, ctx) {
        const richTextEditorContent = ref(null);
        const justPasted = ref(false);
        const editorFocused = ref(false)

        const icons = Quill.import('ui/icons');
        Quill.register("modules/magicUrl", MagicUrl);

        const block = Quill.import('blots/block');
        block.tagName = 'DIV';
        Quill.register(block, true);

        const Parchment = Quill.import("parchment");
        class IndentAttributor extends Parchment.Attributor.Style {
            add(node, value) {
                if (value === 0) {
                    this.remove(node);
                    return true;
                } else {
                    return super.add(node, `${value}em`);
                }
            }
        }

        let IndentStyle = new IndentAttributor("indent", "text-indent", {
            scope: Parchment.Scope.BLOCK,
            whitelist: ["1em", "2em", "3em", "4em", "5em", "6em", "7em", "8em", "9em"]
        });

        Quill.register(IndentStyle, true);

        icons['bold'] = false;
        icons['italic'] = false;
        icons['underline'] = false;
        icons['indent'] = {
            '+1': false,
            '-1': false
        }
        icons['list'] = {
            'bullet': false,
            'ordered': false
        }
        icons['link'] = false;

        const editorOption = {
            modules: {
                magicUrl: {
                    normalizeUrlOptions: {
                        normalizeProtocol: true
                    }
                },
                toolbar: props.toolbar ? props.toolbar : null
            },
            placeholder: props.placeholder,
            bounds: '.rich-text-editor'
        };

        const onInput = (event) => {
            let newInput = event.html

            if (event.text === '\n') {
                ctx.emit('input', '')
            } else {
                ctx.emit('input', newInput)
            }
        }


        onMounted(() => {
            richTextEditorContent.value.quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
                let ops = []
                delta.ops.forEach(op => {
                    if (op.insert && typeof op.insert === 'string') {
                        ops.push({
                            insert: op.insert,
                            attributes: op.attributes ? {
                                list: op.attributes.list,
                                italic: op.attributes.italic,
                                bold: op.attributes.bold,
                                underline: op.attributes.underline,
                                link: op.attributes.link
                            } : null
                        })
                    }
                })
                delta.ops = ops
                return delta
            })

            richTextEditorContent.value.quill.root.addEventListener('paste', (data) => {
                justPasted.value = true
            })
        })

        const focus = (event) => {
            ctx.emit('focus', event)
            editorFocused.value = event
        }

        return {
            editorOption,
            sanitizeHtml,
            richTextEditorContent,
            onInput,
            focus,
            editorFocused
        }
    }
}
</script>

<style lang="scss" scoped>
.quill-editor {
    ::v-deep .ql-container {
        border: 0;

        .ql-editor {
            height: 245px;
            padding: 16px 12px;
            border-radius: 4px;
            border: 1px solid var(--v-g3-base);
            transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
            font-size: 0.875rem !important;

            &.ql-blank::before {
                left: 12px;
                color: var(--v-g4-base)
            }

            &:focus {
                border: 2px solid var(--v-primary-base);
                caret-color: var(--v-primary-base);

                padding: 15px 11px;
            }

            p {
                margin-bottom: 0 !important;
            }
        }

        &.ql-disabled .ql-editor {
            opacity: 0.37;
            border-color: var(--v-g4-base);
        }
    }

    &.ql-error ::v-deep .ql-container .ql-editor {
        border: 2px solid var(--v-error-base) !important;
        caret-color: var(--v-error-base) !important;
    }
}

.custom-label {
    font-size: 12px;
    line-height: 20px;
    font-weight: 600;
}
</style>